import { Component, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { type AbstractControl, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { debounceTime, distinctUntilChanged, Subject, Subscription, take, takeUntil } from 'rxjs';

import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import {
  MemberDetailsFormService,
  TellentAccessService,
  TellentMembersDataFacade,
} from '@core/tellent-logic';
import { AddMemberForm, InviteMethod, LoginType } from '@core/tellent-types';
import { InviteMethodSelectComponent, LoginTypeSelectComponent } from '@core/tellent-ui';
import {
  ButtonComponent,
  DialogRef,
  DialogService,
  DialogWidth,
  DropdownWidth,
  FormErrorDirective,
  FormFieldComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
  ProtectedControlDirective,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

import { EditMemberAdminDialogComponent } from '../../edit-member-admin-dialog/edit-member-admin-dialog.component';
import { ExistingMemberInfoComponent } from '../../existing-member-info/existing-member-info.component';

const removeDuplicateError = (control: AbstractControl<null | string, null | string> | null) => {
  if (control?.errors?.duplicated) {
    const { duplicated, ...errors } = control?.errors;
    control?.setErrors(errors && Object.keys(errors).length ? { ...errors } : null);
  }
};

@Component({
  selector: 'tlc-member-details-form',
  templateUrl: './add-member-details-form.component.html',
  styleUrl: './add-member-details-form.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormFieldComponent,
    TranslatePipe,
    LabelComponent,
    InputComponent,
    InviteMethodSelectComponent,
    LoginTypeSelectComponent,
    ButtonComponent,
    IconComponent,
    ReactiveFormsModule,
    ProtectedControlDirective,
    FormErrorDirective,
    IconButtonComponent,
    ExistingMemberInfoComponent,
  ],
})
export class AddMemberDetailsFormComponent {
  private readonly t = injectTranslate();
  private readonly tellentAccessService = inject(TellentAccessService);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly dialogService = inject(DialogService);
  private readonly untilDestroyed = injectUntilDestroyed();
  private readonly memberDetailsFormService = inject(MemberDetailsFormService);

  protected readonly DropdownWidth = DropdownWidth;
  protected readonly InviteMethod = InviteMethod;
  protected readonly LoginType = LoginType;

  public canManageTellentUsers = this.tellentAccessService.canManageTellentUsers();

  public displayAdvancedForm = input<boolean>(false);
  public parentForm = input.required<FormGroup<AddMemberForm>>();

  public importCsvClick = output<void>();

  public canAddRow = signal(true);

  public emailFieldErrors = {
    email: this.t('tlc.common.errors.email.format'),
    duplicated: this.t('tlc.common.errors.duplicated_email'),
  };

  public loginFieldErrors = {
    required: this.t('tlc.common.errors.required'),
    duplicated: this.t('tlc.common.errors.username.duplicated'),
    unicodeText: this.t('tlc.common.errors.has_to_contain_letter_or_number'),
    alreadyTaken: this.t('tlc.common.errors.username.already_taken'),
  };

  public firstNameFieldErrors = {
    unicodeText: this.t('tlc.common.errors.has_to_contain_letter_or_number'),
  };

  public lastNameFieldErrors = {
    unicodeText: this.t('tlc.common.errors.has_to_contain_letter_or_number'),
  };

  private editDialogTrackReset$: Subject<void> = new Subject();
  private emailDuplicatesDetectionSub: Subscription;
  private loginDuplicatesDetectionSub: Subscription;

  public get members(): any {
    return this.parentForm().controls.members.controls;
  }

  public get inviteMethodControl(): FormControl<InviteMethod | null> {
    return this.parentForm().controls.inviteMethod;
  }

  public get loginTypeControl(): FormControl<LoginType | null> {
    return this.parentForm().controls.loginType;
  }

  public ngOnInit(): void {
    this.inviteMethodControl.valueChanges
      .pipe(this.untilDestroyed)
      .subscribe((inviteMethod: InviteMethod) => {
        this.onInviteMethodChange(inviteMethod);
      });

    this.loginTypeControl.valueChanges
      .pipe(this.untilDestroyed)
      .subscribe((loginType: LoginType) => {
        this.onLoginTypeChange(loginType);
      });

    this.emailDuplicatesDetectionSub = this.watchForDuplicates('email');
  }

  public onInviteMethodChange(inviteMethod: InviteMethod): void {
    switch (inviteMethod) {
      case InviteMethod.Email:
        if (this.loginDuplicatesDetectionSub) this.loginDuplicatesDetectionSub.unsubscribe();
        this.emailDuplicatesDetectionSub = this.watchForDuplicates('email');
        break;

      case InviteMethod.Login:
        if (this.emailDuplicatesDetectionSub) this.emailDuplicatesDetectionSub.unsubscribe();
        this.loginDuplicatesDetectionSub = this.watchForDuplicates('login');
        break;

      default:
        break;
    }

    this.members.forEach((controlGroup: any) =>
      this.memberDetailsFormService.updateMemberGroup(
        controlGroup,
        inviteMethod,
        this.loginTypeControl.value!,
      ),
    );
  }

  public onLoginTypeChange(method: LoginType): void {
    if (this.inviteMethodControl.value === InviteMethod.Email) return;

    this.members.forEach((controlGroup: any) =>
      this.memberDetailsFormService.updateMemberGroup(
        controlGroup,
        this.inviteMethodControl.value!,
        method,
      ),
    );
  }

  public addMemberFormGroup() {
    const form = this.parentForm();
    const memberFormGroup = this.memberDetailsFormService.createMemberFormGroup(
      this.inviteMethodControl.value!,
      this.loginTypeControl.value!,
    );
    form.controls.members.push(memberFormGroup);
    this.canAddRow.set(form.controls.members.length < 50);
  }

  public removeMemberFormGroup(index: number) {
    this.parentForm().controls.members.removeAt(index);
    this.canAddRow.set(this.parentForm().controls.members.length < 50);
  }

  public editAccess(memberGuid: string) {
    this.editDialogTrackReset$.next();
    const ref: DialogRef<EditMemberAdminDialogComponent> = this.dialogService.openCustom(
      EditMemberAdminDialogComponent,
      {
        width: DialogWidth.Medium,
        containerOptions: { fullscreenOnMobile: true },
        closeGuard: () => ref.componentInstance.protectedControls(() => true),
      },
      { memberId: memberGuid },
    );

    this.trackUpdateSuccess(memberGuid);
  }

  public navigateToCsvImport(): void {
    this.importCsvClick.emit();
  }

  private trackUpdateSuccess(memberGuid: string): void {
    this.membersDataFacade.manageSeatsSuccess$
      .pipe(take(1), takeUntil(this.editDialogTrackReset$), this.untilDestroyed)
      .subscribe(() => {
        this.membersDataFacade.fetchOne(memberGuid);
      });
  }

  private watchForDuplicates(controlName: string): Subscription {
    return this.parentForm()
      .controls.members.valueChanges.pipe(
        distinctUntilChanged(),
        debounceTime(300),
        this.untilDestroyed,
      )
      .subscribe(members => {
        const controls = this.parentForm().controls.members.controls;
        const watchedControlArray = controls.map(control => control.get(controlName)?.value);

        if (members.length === 1) {
          const control = this.parentForm().controls.members.controls[0].get(controlName);
          if (control) removeDuplicateError(control);
        } else {
          controls.forEach(control => {
            const watchedControl = control.get(controlName);

            if (
              watchedControlArray.some(
                (controlValue, i) =>
                  !!controlValue &&
                  watchedControlArray.indexOf(controlValue) !== i &&
                  controlValue === watchedControl?.value,
              )
            ) {
              watchedControl?.setErrors({ duplicated: true });
            } else {
              removeDuplicateError(watchedControl);
            }
          });
        }
        this.updateFieldsDisabledState();
      });
  }

  // Function to update the disabled state of firstName and lastName fields
  private updateFieldsDisabledState(): void {
    this.members.forEach((controlGroup: any) => {
      const emailControl = controlGroup.controls.email;

      // Wait for async validators to complete
      if (emailControl.status === 'PENDING') {
        emailControl.statusChanges.pipe(take(1)).subscribe(() => {
          this.disableFieldsIfNeeded(controlGroup);
        });
      } else {
        this.disableFieldsIfNeeded(controlGroup);
      }
    });
  }

  private disableFieldsIfNeeded(controlGroup: any): void {
    const hasExistingMember = !!controlGroup.controls.email.getError('existingMember');
    const firstNameControl = controlGroup.controls.firstName;
    const lastNameControl = controlGroup.controls.lastName;

    if (hasExistingMember) {
      firstNameControl.disable({ emitEvent: false });
      lastNameControl.disable({ emitEvent: false });
    } else {
      firstNameControl.enable({ emitEvent: false });
      lastNameControl.enable({ emitEvent: false });
    }
  }
}
